import { locatesWorkerManifest } from './contract';
import type { LocatesWorkerContract } from './contract';
import { TypedContainer } from '@thinkalpha/common/ioc/TypedContainer.js';
import { inject, injectable } from 'inversify';
import { skipAutoDispose } from 'src/features/ioc';
import { connectWorker } from 'src/services/workerHost/client';
import type { WorkerClient, WorkerStateRelayBindings } from 'src/services/workerHost/client';
import type { WorkerStateRelay } from 'src/services/workerHost/stateRegistry';
import { dedicatedWorkerTransport } from 'src/services/workerHost/transports/dedicatedWorkerTransport';

export interface LocatesWorkerLease extends Disposable {
    readonly client: WorkerClient<LocatesWorkerContract>;
}

/** One shared LAB store per UI container; the last consumer releases its hosted worker. */
@injectable()
export class LocatesWorkerConnection implements Disposable {
    readonly [skipAutoDispose] = true;
    readonly #resources = new DisposableStack();
    readonly #relays = new TypedContainer<WorkerStateRelayBindings>();
    readonly #consumers = new Set<symbol>();
    #client: WorkerClient<LocatesWorkerContract> | undefined;

    constructor(
        @inject('WorkerStateRelay:session') session: WorkerStateRelay<'session'>,
        @inject('WorkerStateRelay:featureFlags') flags: WorkerStateRelay<'featureFlags'>,
        @inject('WorkerStateRelay:user') user: WorkerStateRelay<'user'>,
    ) {
        this.#relays.bind('WorkerStateRelay:session').toConstantValue(session);
        this.#relays.bind('WorkerStateRelay:featureFlags').toConstantValue(flags);
        this.#relays.bind('WorkerStateRelay:user').toConstantValue(user);
        this.#resources.defer(() => {
            this.#consumers.clear();
            this.#client?.[Symbol.dispose]();
            this.#client = undefined;
        });
    }

    acquire(): LocatesWorkerLease {
        if (this.#resources.disposed) throw new Error('Locates worker connection is disposed');
        this.#client ??= connectWorker(
            this.#relays,
            locatesWorkerManifest,
            dedicatedWorkerTransport(() => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })),
            { preReady: 'buffer' },
        );
        const key = Symbol();
        this.#consumers.add(key);
        return {
            client: this.#client,
            [Symbol.dispose]: () => {
                if (this.#consumers.delete(key) && this.#consumers.size === 0) {
                    this.#client?.[Symbol.dispose]();
                    this.#client = undefined;
                }
            },
        };
    }

    [Symbol.dispose](): void {
        this.#resources.dispose();
    }
}
